import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AgentHelpModal from './AgentHelpModal';

type AgentCreationModeDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreateFromScratch: () => void;
  onChooseTemplate: () => void;
  onDuplicateFromAgent: () => void;
  onManageWithAI: () => void;
};

const OptionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}> = ({ icon, title, subtitle, onClick }) => (
  <Card elevation={4} sx={{ borderRadius: 3, height: '100%' }}>
    <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 1
        }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#65f0c5' : 'primary.light',
            color: (theme) => theme.palette.mode === 'dark' ? '#000' : 'primary.contrastText',
            mb: 1
          }}>
            {icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const AgentCreationModeDialog: React.FC<AgentCreationModeDialogProps> = ({
  open,
  onClose,
  onCreateFromScratch,
  onChooseTemplate,
  onDuplicateFromAgent,
  onManageWithAI
}) => {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        Escolha como deseja criar o agente
        <Box>
          <IconButton aria-label="help" onClick={() => setHelpOpen(true)} size="small" sx={{ mr: 0.5 }}>
            <HelpOutlineIcon />
          </IconButton>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2
        }}>
          <Box>
            <OptionCard
              icon={<CreateIcon fontSize="large" />}
              title="Criar do zero"
              subtitle="Configure todas as etapas manualmente"
              onClick={onCreateFromScratch}
            />
          </Box>
          <Box>
            <OptionCard
              icon={<ViewModuleIcon fontSize="large" />}
              title="Escolher template"
              subtitle="Selecione um modelo pré-configurado"
              onClick={onChooseTemplate}
            />
          </Box>
          <Box>
            <OptionCard
              icon={<ContentCopyIcon fontSize="large" />}
              title="Duplicar de outro agente"
              subtitle="Copie as configurações de um agente existente"
              onClick={onDuplicateFromAgent}
            />
          </Box>
          <Box>
            <OptionCard
              icon={<AutoAwesomeIcon fontSize="large" />}
              title="Gerenciar pela IA"
              subtitle="Deixe a IA gerenciar e otimizar seus agentes"
              onClick={onManageWithAI}
            />
          </Box>
        </Box>
      </DialogContent>
      <AgentHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} topic="creation-mode" />
    </Dialog>
  );
};

export default AgentCreationModeDialog;


